import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { unsubscribeAPI } from "./UnsubscribeAPI";

const SRI_ORANGE = "#FF6E40";

// Public page reached from the "Unsubscribe" link in the auction-update emails.
// The signed token in the URL identifies the subscriber; core-api verifies it.
// Two-step by design: we validate + show the email, then the user confirms
// (POST) — so email link scanners that pre-fetch the GET can't unsubscribe
// anyone by accident.
function Unsubscribe() {
  useDocumentTitle("Unsubscribe");

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // loading | invalid | ready | submitting | done | error
  const [status, setStatus] = useState("loading");
  const [email, setEmail] = useState(null);
  const [alreadyOff, setAlreadyOff] = useState(false);

  useEffect(() => {
    let active = true;
    if (!token) {
      setStatus("invalid");
      return;
    }
    unsubscribeAPI
      .getInfo(token)
      .then((data) => {
        if (!active) return;
        if (data?.valid) {
          setEmail(data.email || null);
          setStatus("ready");
        } else {
          setStatus("invalid");
        }
      })
      .catch(() => {
        if (active) setStatus("invalid");
      });
    return () => {
      active = false;
    };
  }, [token]);

  const handleConfirm = async () => {
    setStatus("submitting");
    try {
      const result = await unsubscribeAPI.confirm(token);
      setAlreadyOff(result?.status === "already");
      if (result?.email) setEmail(result.email);
      setStatus("done");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <div className="w-100 d-flex justify-content-center align-items-start p-4">
      <div
        className="bg-white rounded-3 shadow-sm p-4 p-md-5 my-5 text-center"
        style={{ maxWidth: "520px", width: "100%" }}
      >
        <h1 className="h3 fw-bold mb-3">Email Subscription</h1>

        {status === "loading" && (
          <div className="py-4 text-muted">
            <div className="spinner-border" role="status" aria-hidden="true" />
            <p className="mt-3 mb-0">Loading…</p>
          </div>
        )}

        {status === "invalid" && (
          <p className="text-muted mb-0">
            This unsubscribe link is invalid or has expired. If you'd like to
            manage your email preferences, please sign in to your SRI account.
          </p>
        )}

        {(status === "ready" || status === "submitting") && (
          <>
            <p className="mb-4">
              {email ? (
                <>
                  Unsubscribe <strong>{email}</strong> from SRI, Incorporated
                  auction update emails?
                </>
              ) : (
                <>Unsubscribe from SRI, Incorporated auction update emails?</>
              )}
            </p>
            <button
              type="button"
              className="btn btn-lg text-white fw-bold px-4"
              style={{ backgroundColor: SRI_ORANGE }}
              onClick={handleConfirm}
              disabled={status === "submitting"}
            >
              {status === "submitting" ? "Unsubscribing…" : "Confirm Unsubscribe"}
            </button>
            <p className="text-muted small mt-3 mb-0">
              You'll stop receiving the weekly "Upcoming Auctions" email. You can
              re-subscribe anytime from your account settings.
            </p>
          </>
        )}

        {status === "done" && (
          <p className="mb-0">
            {alreadyOff ? (
              <>
                You're already unsubscribed
                {email ? (
                  <>
                    {" "}
                    (<strong>{email}</strong>)
                  </>
                ) : null}
                . You won't receive auction update emails.
              </>
            ) : (
              <>
                You've been unsubscribed
                {email ? (
                  <>
                    {" "}
                    (<strong>{email}</strong>)
                  </>
                ) : null}
                . You'll no longer receive auction update emails from SRI,
                Incorporated.
              </>
            )}
          </p>
        )}

        {status === "error" && (
          <>
            <p className="text-muted mb-3">
              Something went wrong while updating your preferences. Please try
              again.
            </p>
            <button
              type="button"
              className="btn btn-lg text-white fw-bold px-4"
              style={{ backgroundColor: SRI_ORANGE }}
              onClick={handleConfirm}
            >
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default Unsubscribe;
